'use client';

import Button from '@/components/ui/Button';
import Dialog from '@/components/ui/Dialog';
import { formatMNT } from '@/lib/utils/formatters';
import { plusCopy } from './plusCopy';

export function PlusPaymentDialog({
  isOpen,
  isEnglish,
  price,
  netBalance,
  netBonusBalance,
  isPaying,
  onClose,
  onPay,
  onRecharge,
}: {
  isOpen: boolean;
  isEnglish: boolean;
  price: number;
  netBalance: number;
  netBonusBalance: number;
  isPaying: boolean;
  onClose: () => void;
  onPay: () => void;
  onRecharge: () => void;
}) {
  const t = isEnglish ? plusCopy.en : plusCopy.mn;
  const totalBalance = netBalance + netBonusBalance;
  const hasEnough = totalBalance >= price;
  const remaining = totalBalance - price;
  const shortfall = price - totalBalance;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={hasEnough ? t.payTitle : t.insufficientTitle}
      maxWidth="sm"
    >
      <div className="space-y-4 text-sm">
        <div className="space-y-3 rounded-xl bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">{t.payAmount}</span>
            <span className="font-semibold text-gray-900">
              {formatMNT(price)}
            </span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <span className="text-gray-600">{t.totalBalance}</span>
            <div className="text-right">
              <span className="block font-semibold text-gray-900">
                {formatMNT(totalBalance)}
              </span>
              <span className="text-xs text-gray-500">
                ({t.main}: {formatMNT(netBalance)} + {t.bonus}:{' '}
                {formatMNT(netBonusBalance)})
              </span>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium text-gray-900">
                {hasEnough ? t.remaining : t.shortfall}
              </span>
              <span
                className={
                  hasEnough
                    ? 'font-bold text-success-600'
                    : 'font-bold text-error-600'
                }
              >
                {formatMNT(hasEnough ? remaining : shortfall)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPaying}
            className="h-9"
          >
            {t.cancel}
          </Button>
          {hasEnough ? (
            <Button
              variant="primary"
              onClick={onPay}
              disabled={isPaying}
              loading={isPaying}
              className="h-9"
            >
              {t.pay}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={onRecharge}
              disabled={isPaying}
              className="h-9"
            >
              {t.recharge}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
